import {
  ChipStatus,
  EBundlesViewModel,
  ERollingOffersInternalViewModel
} from '@appcharge/shared-ui';
import { EBadgeType } from 'common/contracts';
import {
  EBundlesInternalViewModel,
  EProductPriority,
  ESaleDiscountDisplayType,
  ESpecialOfferInternalViewModel,
  OfferType,
  SortingOffersValue
} from 'constants/enums';

import { OfferStatus } from './Offer.types';

export const BADGE_TYPE_FILTER_ALL = 'both' as const;

export type BadgeTypeFilter = EBadgeType | typeof BADGE_TYPE_FILTER_ALL;

export const OFFER_STATUS_PRIORITY: Record<OfferStatus, number> = {
  Active: 0,
  Interval: 1,
  Permanent: 2,
  Inactive: 3
};

export const OFFER_STATUS_CHIP_MAPPING: Record<OfferStatus, ChipStatus> = {
  Active: 'success',
  Interval: 'optional',
  Permanent: 'active',
  Inactive: 'danger'
};

export const CLIENT_SIDE_SORT_VALUES = new Set<SortingOffersValue>([
  SortingOffersValue.STATUS,
  SortingOffersValue.SCHEDULE
]);

export const SALE_DISCOUNT_TYPE_OPTIONS = [
  { value: ESaleDiscountDisplayType.PERCENTAGE, label: 'Percentage' },
  { value: ESaleDiscountDisplayType.MULTIPLIER, label: 'Multiplier' },
  { value: ESaleDiscountDisplayType.FIXED_AMOUNT, label: 'Fixed amount' }
];

export const DEFAULT_OFFER_DESIGN_EXTERNAL_ID: Record<string, string> = {
  [OfferType.BUNDLE]: 'Default_Bundle',
  [OfferType.SPECIAL_OFFER]: 'Default_SpecialOffer',
  [OfferType.POPUP]: 'Default_PopUp',
  [OfferType.ROLLING_OFFER]: 'Default_RollingOffer',
  [OfferType.BUY_ONE_GET_MANY]: 'Default_BuyOneGetMany'
};

export const MAP_VIEW_MODEL_TO_BADGE_TYPE: Record<
  EBundlesViewModel,
  EBadgeType
> = {
  [EBundlesViewModel.LINEAR]: EBadgeType.EMBLEM,
  [EBundlesViewModel.THREE_IN_ONE]: EBadgeType.RIBBON,
  [EBundlesViewModel.SIX_IN_ONE]: EBadgeType.EMBLEM,
  [EBundlesViewModel.TWO_IN_ONE]: EBadgeType.RIBBON,
  [EBundlesViewModel.TWO_IN_ONE_DYNAMIC]: EBadgeType.RIBBON
};

export const TRANSLATE_MOCK_MAP: Record<string, string> = {
  'footer.privacy': 'Privacy Policy',
  'footer.termsAndConditions': 'Terms and Conditions',
  'footer.support': 'Support',
  'footer.cookieSettings': 'Cookie Settings',
  'footer.legalOver18':
    'Must be 18+ to access/play game. No opportunity to win real money or prizes. In-app purchases are optional and virtual items are licenses and not exchangeable for real money/prizes. Practice or success at social gaming does not imply future success at gambling.',
  'footer.byClicking':
    'By clicking on a coin package you acknowledge that you have read and agree to the',
  'footer.privacyPolicy': 'Privacy Policy',
  'footer.appstore': 'App Store',
  'footer.googleplay': 'Google Play',
  includes: 'Includes',
  'shop.addToHomeScreen': 'Add to home screen'
};

export const PRODUCT_PRIORITY_OPTIONS = Object.values(EProductPriority).map(
  (priority) => ({
    value: priority,
    label: priority
  })
);

export const LOCALIZED_PRICE_ERROR = 'Price point is not localized';

export const EMPTY_TAB = { content: 'All', key: '0', value: '0' };

export const BUNDLE_MODEL_BADGE_MAP: Partial<
  Record<EBundlesInternalViewModel, BadgeTypeFilter>
> = {
  [EBundlesInternalViewModel.HEART_OF_VEGAS]: BADGE_TYPE_FILTER_ALL,
  [EBundlesInternalViewModel.LIGHTNING_LINK_CASINO]: BADGE_TYPE_FILTER_ALL,
  [EBundlesInternalViewModel.DYNAMIC_HEART_OF_VEGAS]: BADGE_TYPE_FILTER_ALL,
  [EBundlesInternalViewModel.LIGHTNING_LINK_CASINO2]: BADGE_TYPE_FILTER_ALL,
  [EBundlesInternalViewModel.PLAYDOG]: BADGE_TYPE_FILTER_ALL,
  [EBundlesInternalViewModel.STUDIO_DEFAULT_LINEAR]: BADGE_TYPE_FILTER_ALL,
  [EBundlesInternalViewModel.STUDIO_DEFAULT_TWO_IN_ONE]: BADGE_TYPE_FILTER_ALL,
  [EBundlesInternalViewModel.STUDIO_DEFAULT_THREE_IN_ONE]:
    BADGE_TYPE_FILTER_ALL,
  [EBundlesInternalViewModel.DRIMAGE]: BADGE_TYPE_FILTER_ALL,
  [EBundlesInternalViewModel.GHOST]: EBadgeType.EMBLEM,
  [EBundlesInternalViewModel.JUNES_JOURNEY]: EBadgeType.EMBLEM,
  [EBundlesInternalViewModel.CANDY_CRUSH]: EBadgeType.EMBLEM,
  [EBundlesInternalViewModel.LOAD_COMPLETE]: EBadgeType.EMBLEM,
  [EBundlesInternalViewModel.ROYAL_MATCH]: EBadgeType.EMBLEM,
  [EBundlesInternalViewModel.PLAY_SIMPLE]: EBadgeType.RIBBON,
  [EBundlesInternalViewModel.BANDITOS]: EBadgeType.RIBBON,
  [EBundlesInternalViewModel.COOKING_MADNESS]: EBadgeType.EMBLEM,
  [EBundlesInternalViewModel.BEACHBUM]: EBadgeType.EMBLEM,
  [EBundlesInternalViewModel.KONAMI_SLOTS]: EBadgeType.RIBBON,
  [EBundlesInternalViewModel.POP_SLOTS]: EBadgeType.RIBBON,
  [EBundlesInternalViewModel.MGM]: EBadgeType.RIBBON,
  [EBundlesInternalViewModel.MY_VEGAS_SLOTS]: EBadgeType.RIBBON,
  [EBundlesInternalViewModel.MY_VEGAS_CLASSIC]: EBadgeType.RIBBON
};

export const PROMOTION_MODEL_BADGE_MAP: Partial<
  Record<ESpecialOfferInternalViewModel, BadgeTypeFilter>
> = {
  [ESpecialOfferInternalViewModel.STUDIO_DEFAULT]: BADGE_TYPE_FILTER_ALL,
  [ESpecialOfferInternalViewModel.DRIMAGE]: BADGE_TYPE_FILTER_ALL,
  [ESpecialOfferInternalViewModel.GHOST]: EBadgeType.EMBLEM,
  [ESpecialOfferInternalViewModel.MATCH_MASTERS]: EBadgeType.EMBLEM,
  [ESpecialOfferInternalViewModel.GENNMU]: EBadgeType.EMBLEM,
  [ESpecialOfferInternalViewModel.CASHMAN_CASINO]: EBadgeType.EMBLEM,
  [ESpecialOfferInternalViewModel.MIGHTY_FU]: EBadgeType.EMBLEM,
  [ESpecialOfferInternalViewModel.LOAD_COMPLETE]: EBadgeType.EMBLEM,
  [ESpecialOfferInternalViewModel.ROYAL_MATCH]: EBadgeType.EMBLEM,
  [ESpecialOfferInternalViewModel.PLAY_SIMPLE]: EBadgeType.RIBBON,
  [ESpecialOfferInternalViewModel.BANDITOS]: EBadgeType.RIBBON,
  [ESpecialOfferInternalViewModel.GHOST_DOUBLE_HIT_CASINO]: EBadgeType.RIBBON,
  [ESpecialOfferInternalViewModel.CANDY_CRUSH]: EBadgeType.EMBLEM,
  [ESpecialOfferInternalViewModel.BEACHBUM]: EBadgeType.EMBLEM,
  [ESpecialOfferInternalViewModel.COOKING_MADNESS]: BADGE_TYPE_FILTER_ALL,
  [ESpecialOfferInternalViewModel.KONAMI_SLOTS]: EBadgeType.RIBBON,
  [ESpecialOfferInternalViewModel.POP_SLOTS]: EBadgeType.RIBBON,
  [ESpecialOfferInternalViewModel.MGM]: EBadgeType.RIBBON,
  [ESpecialOfferInternalViewModel.MY_VEGAS_SLOTS]: EBadgeType.RIBBON,
  [ESpecialOfferInternalViewModel.MY_VEGAS_CLASSIC]: EBadgeType.RIBBON
};

export const ROLLING_OFFER_MODEL_BADGE_MAP: Partial<
  Record<ERollingOffersInternalViewModel, EBadgeType>
> = {
  [ERollingOffersInternalViewModel.KONAMI_SLOTS]: EBadgeType.RIBBON,
};
